import React from 'react';

/**
 * TimeProposalsSection - Visar tidsförslag från byggkonsult
 * Används i EstimatView för att visa och hantera uppföljningstidsförslag
 * 
 * @param {Object} builderProposals - Byggkonsultens tidsförslag
 * @param {string} acceptingProposal - ID för förslag som accepteras (för loading state)
 * @param {Function} onAcceptProposal - Callback när förslag accepteras
 * @param {Object} showTimeProposalReply - State för vilka svar som visas
 * @param {Object} timeProposalReplyText - Text för svar
 * @param {Function} onTimeProposalReply - Callback för att skicka svar
 * @param {boolean} sendingTimeProposalReply - Loading state för svar
 * @param {Function} setShowTimeProposalReply - Setter för att visa/dölja svarsfält
 * @param {Function} setTimeProposalReplyText - Setter för svarstext
 * @param {boolean} expanded - Om sektionen är expanderad
 * @param {Function} onToggle - Callback för att toggla expansion
 */
const TimeProposalsSection = ({
  builderProposals,
  acceptingProposal,
  onAcceptProposal,
  showTimeProposalReply,
  timeProposalReplyText,
  onTimeProposalReply,
  sendingTimeProposalReply,
  setShowTimeProposalReply,
  setTimeProposalReplyText,
  expanded,
  onToggle,
}) => {
  if (!builderProposals || !builderProposals.proposals || builderProposals.proposals.length === 0) {
    // Om kunden har accepterat ett förslag, visa bekräftelse istället
    if (builderProposals?.customerResponse?.status === 'accepted' && builderProposals?.customerResponse?.acceptedProposal) {
      const acceptedProposal = builderProposals.customerResponse.acceptedProposal;
      return (
        <div className="mb-6 bg-green-50 rounded-lg shadow-sm border border-green-200 p-4">
          <div className="flex items-start gap-3">
            <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
              </svg>
            </div>
            <div>
              <h4 className="text-sm font-medium text-green-900 mb-1">
                Du har accepterat uppföljningsmöte
              </h4>
              <p className="text-sm text-green-700 mb-2">
                <strong>{acceptedProposal.displayText}</strong>
              </p>
              <p className="text-xs text-green-600">
                Byggkonsulten kommer att bekräfta bokningen och skicka en Teams-inbjudan till dig.
              </p>
            </div>
          </div>
        </div>
      );
    }
    return null;
  }

  // Dölj hela sektionen om kunden redan har svarat (nekat förslagen)
  if (builderProposals.customerResponse?.status === 'replied') {
    return null;
  }

  // Om kunden har accepterat ett förslag, visa bekräftelse
  if (builderProposals.customerResponse?.status === 'accepted') {
    const acceptedProposal = builderProposals.customerResponse.acceptedProposal;
    return (
      <div className="mb-6 bg-green-50 rounded-lg shadow-sm border border-green-200 p-4">
        <div className="flex items-start gap-3">
          <div className="flex-shrink-0 w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
            <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 13l4 4L19 7" />
            </svg>
          </div>
          <div>
            <h4 className="text-sm font-medium text-green-900 mb-1">
              Du har accepterat uppföljningsmöte
            </h4>
            <p className="text-sm text-green-700 mb-2">
              <strong>{acceptedProposal?.displayText || 'Tid accepterad'}</strong>
            </p>
            <p className="text-xs text-green-600">
              Byggkonsulten kommer att bekräfta bokningen och skicka en Teams-inbjudan till dig.
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-6 bg-white rounded-lg shadow-sm border border-blue-200">
      {/* Accordion header - klickbar */}
      <div
        className="flex items-center justify-between p-4 cursor-pointer select-none bg-blue-50 hover:bg-blue-100 transition-colors"
        onClick={onToggle}
        role="button"
        tabIndex={0}
        aria-expanded={expanded}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            onToggle();
          }
        }}
      >
        <h2 className="text-lg font-semibold text-blue-800">
          📅 Uppföljningsmöte
        </h2>
        <div className="flex items-center gap-3">
          <span className="text-sm text-blue-700 font-medium">
            {builderProposals.proposals?.length || 0} föreslagna tider
          </span>
          <svg
            className={`w-5 h-5 text-blue-600 transition-transform duration-200 ${expanded ? 'rotate-180' : ''}`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {/* Accordion content - expanderbar */}
      {expanded && (
        <div className="p-4 bg-blue-50 border-t border-blue-200">
          <p className="text-sm text-blue-800 mb-3">
            {builderProposals.message || 'Byggkonsulten har föreslagit följande tidpunkter för uppföljning:'}
          </p>

          <div className="space-y-2">
            {builderProposals.proposals.map((proposal, index) => (
              <div key={index} className="bg-white p-3 rounded border border-blue-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <svg className="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <span className="text-sm font-medium text-gray-900">
                      {proposal.displayText}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowTimeProposalReply(prev => ({ ...prev, [index]: !prev[index] }));
                      }}
                      className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-xs font-medium rounded transition-colors"
                    >
                      {showTimeProposalReply[index] ? 'Ångra' : 'Neka'}
                    </button>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onAcceptProposal(proposal);
                      }}
                      disabled={acceptingProposal === proposal.displayText}
                      className="px-3 py-1 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-xs font-medium rounded"
                    >
                      {acceptingProposal === proposal.displayText ? 'Accepterar...' : 'Acceptera'}
                    </button>
                  </div>
                </div>

                {/* Svarsfält */}
                {showTimeProposalReply[index] && (
                  <div className="mt-3 space-y-2">
                    <textarea
                      value={timeProposalReplyText[index] || ''}
                      onChange={(e) => setTimeProposalReplyText(prev => ({ ...prev, [index]: e.target.value }))}
                      placeholder="Skriv varför du inte kan ta mötet dessa tider..."
                      className="w-full px-3 py-2 border border-gray-300 rounded text-sm focus:ring-2 focus:ring-leadme-pink focus:border-leadme-pink"
                      rows={3}
                    />
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        onTimeProposalReply(index);
                      }}
                      disabled={sendingTimeProposalReply || !timeProposalReplyText[index]?.trim()}
                      className="px-4 py-2 bg-leadme-pink hover:bg-leadme-pink-dark disabled:bg-gray-400 disabled:cursor-not-allowed text-white text-sm font-medium rounded"
                    >
                      {sendingTimeProposalReply ? 'Skickar...' : 'Skicka svar'}
                    </button>
                  </div>
                )}
              </div>
            ))}
          </div>

          {builderProposals.sentAt && (
            <p className="text-xs text-blue-600 mt-3">
              Skickat: {new Date(builderProposals.sentAt).toLocaleString('sv-SE')}
            </p>
          )}
        </div>
      )}
    </div>
  );
};

export default TimeProposalsSection;

